import { useState, useEffect, useRef, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { LogoSvg } from "../svgs/LogoSvg";
import { navigationMenus, PATH_CONTACT } from "../../utils/constants";
import { PrimaryButton } from "../common/PrimaryButton";
import { Bars3Icon, XMarkIcon, ChevronDownIcon } from "@heroicons/react/24/outline";
import { useBodyScrollLock } from "../../hooks/useBodyScrollLock";
import { MAIN_CONTENT_ID } from "../../utils/a11y";

type DropdownKey = "services" | "about";

const CLOSED_MOBILE_SECTIONS = { services: false, about: false };

export const Header = () => {
  const location = useLocation();
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const mobilePanelRef = useRef<HTMLDivElement>(null);
  const dropdownButtonRefs = useRef<Partial<Record<DropdownKey, HTMLButtonElement | null>>>({});
  const suppressFocusOpenRef = useRef(false);
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileSectionsOpen, setMobileSectionsOpen] = useState(CLOSED_MOBILE_SECTIONS);
  const [openDropdown, setOpenDropdown] = useState<DropdownKey | null>(null);

  useBodyScrollLock(mobileMenuOpen);

  useEffect(() => {
    if (mobilePanelRef.current) {
      mobilePanelRef.current.inert = !mobileMenuOpen;
    }
  }, [mobileMenuOpen]);

  useEffect(() => {
    if (!mobileMenuOpen) return;

    const panel = mobilePanelRef.current;
    if (!panel) return;

    const focusableSelector = 'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';
    const getFocusables = () => {
      const panelEls = Array.from(panel.querySelectorAll<HTMLElement>(focusableSelector)).filter(
        (el) => !el.hasAttribute("disabled") && el.tabIndex !== -1,
      );
      const toggle = menuButtonRef.current;
      return toggle ? [toggle, ...panelEls] : panelEls;
    };

    const main = document.getElementById(MAIN_CONTENT_ID);
    const footer = document.querySelector("footer");
    if (main) main.inert = true;
    if (footer) (footer as HTMLElement).inert = true;

    requestAnimationFrame(() => {
      getFocusables()[0]?.focus();
    });

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Tab") return;
      const focusables = getFocusables();
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      if (main) main.inert = false;
      if (footer) (footer as HTMLElement).inert = false;
    };
  }, [mobileMenuOpen]);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    setMobileSectionsOpen(CLOSED_MOBILE_SECTIONS);
    setOpenDropdown(null);
  }, [location.pathname]);

  const closeMobileMenu = useCallback(() => {
    setMobileMenuOpen(false);
    setMobileSectionsOpen(CLOSED_MOBILE_SECTIONS);
    menuButtonRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (mobileMenuOpen) {
        closeMobileMenu();
        return;
      }
      if (openDropdown) {
        const key = openDropdown;
        const trigger = dropdownButtonRefs.current[key];
        setOpenDropdown(null);
        if (trigger && document.activeElement !== trigger) {
          suppressFocusOpenRef.current = true;
          trigger.focus();
        }
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [mobileMenuOpen, openDropdown, closeMobileMenu]);

  useEffect(() => {
    if (!openDropdown) return;
    const handlePointerDown = (e: PointerEvent) => {
      const target = e.target as Element;
      if (target.closest("[data-nav-dropdown]")) return;
      setOpenDropdown(null);
    };
    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, [openDropdown]);

  const isMenuActive = (items: { href: string }[]) =>
    items.some((item) => location.pathname === item.href);

  const toggleDropdown = (key: DropdownKey) => {
    setOpenDropdown((prev) => (prev === key ? null : key));
  };

  const renderDesktopDropdown = (
    key: DropdownKey,
    label: string,
    items: { name: string; href: string }[],
    minWidth: string,
    gridCols: string,
  ) => {
    const panelId = `nav-dropdown-${key}`;
    const isOpen = openDropdown === key;
    const active = isMenuActive(items);

    return (
      <div
        data-nav-dropdown
        className="relative"
        onMouseEnter={() => {
          if (window.matchMedia("(hover: hover) and (pointer: fine)").matches) {
            setOpenDropdown(key);
          }
        }}
        onMouseLeave={(e) => {
          if (!window.matchMedia("(hover: hover) and (pointer: fine)").matches) return;
          const container = e.currentTarget;
          requestAnimationFrame(() => {
            if (container.contains(document.activeElement)) return;
            setOpenDropdown((current) => (current === key ? null : current));
          });
        }}
        onBlur={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget as Node)) {
            setOpenDropdown((current) => (current === key ? null : current));
          }
        }}
      >
        <button
          type="button"
          ref={(el) => {
            dropdownButtonRefs.current[key] = el;
          }}
          id={`nav-trigger-${key}`}
          aria-expanded={isOpen}
          aria-controls={panelId}
          onClick={() => toggleDropdown(key)}
          onFocus={() => {
            if (suppressFocusOpenRef.current) {
              suppressFocusOpenRef.current = false;
              return;
            }
            setOpenDropdown(key);
          }}
          className={`relative inline-flex items-center gap-1 px-2 py-2 text-sm lg:text-base transition-colors group min-h-11 ${
            active
              ? "text-ergo-600 dark:text-ergo-400 font-medium"
              : "text-gray-600 dark:text-gray-300 hover:text-ergo-600 dark:hover:text-ergo-400 font-normal"
          }`}
        >
          {label}
          <ChevronDownIcon className={`h-4 w-4 transition-transform ${isOpen ? "rotate-180" : ""}`} aria-hidden="true" />
          <span
            className={`absolute bottom-0 left-0 w-full h-0.5 rounded-full transform origin-left transition-transform duration-300 ease-out ${
              active ? "bg-ergo-400 scale-x-100" : "bg-ergo-300 scale-x-0 group-hover:scale-x-100"
            }`}
          />
        </button>
        <div
          id={panelId}
          className={`${isOpen ? "visible opacity-100" : "invisible opacity-0 pointer-events-none"} transition-all duration-200 absolute left-1/2 -translate-x-1/2 top-full pt-3 ${minWidth}`}
        >
          <div className="rounded-2xl border border-gray-200/60 dark:border-gray-700/40 bg-white dark:bg-gray-900 shadow-lg p-3">
            <div className={`grid ${gridCols} gap-1`}>
              {items.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`block px-4 py-3 rounded-lg transition-colors ${
                    location.pathname === item.href
                      ? "bg-ergo-50/80 text-ergo-700 dark:bg-gray-800/60 dark:text-ergo-300"
                      : "text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/60"
                  }`}
                  onClick={() => setOpenDropdown(null)}
                >
                  {item.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderMobileAccordion = (
    key: keyof typeof mobileSectionsOpen,
    label: string,
    items: { name: string; href: string }[],
    gridCols: string,
  ) => {
    const panelId = `mobile-nav-${key}`;
    const isOpen = mobileSectionsOpen[key];
    const active = isMenuActive(items);

    return (
      <div>
        <button
          type="button"
          aria-expanded={isOpen}
          aria-controls={panelId}
          onClick={() => setMobileSectionsOpen((s) => ({ ...s, [key]: !s[key] }))}
          className={`w-full flex items-center justify-between px-4 py-3 rounded-xl border min-h-11 ${
            active
              ? "bg-ergo-50/50 dark:bg-gray-800/50 border-ergo-200 dark:border-ergo-700/40"
              : "bg-white dark:bg-gray-900 border-gray-200/60 dark:border-gray-700/40"
          }`}
        >
          <span
            className={`text-base font-medium ${
              active ? "text-ergo-600 dark:text-ergo-400" : "text-gray-800 dark:text-gray-200"
            }`}
          >
            {label}
          </span>
          <ChevronDownIcon
            className={`h-5 w-5 text-gray-400 transition-transform ${isOpen ? "rotate-180" : ""}`}
            aria-hidden="true"
          />
        </button>
        <div
          id={panelId}
          aria-hidden={!isOpen}
          className={`grid transition-all duration-300 ${isOpen ? "grid-rows-[1fr] opacity-100 mt-2" : "grid-rows-[0fr] opacity-0 invisible pointer-events-none"}`}
        >
          <div className="overflow-hidden">
            <div className={`grid ${gridCols} gap-2`}>
              {items.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  tabIndex={isOpen ? undefined : -1}
                  className={`block px-4 py-3 rounded-lg transition-colors ${
                    location.pathname === item.href
                      ? "bg-ergo-50/80 text-ergo-700 dark:bg-gray-800/60 dark:text-ergo-300"
                      : "text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-800/30 hover:bg-gray-100 dark:hover:bg-gray-800/60"
                  }`}
                  onClick={closeMobileMenu}
                >
                  {item.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="pt-safe">
        <div className="h-16 sm:h-20" aria-hidden="true" />
      </div>

      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 pt-safe
          bg-white dark:bg-gray-900 border-b border-gray-200/60 dark:border-gray-700/40
          ${scrolled ? "shadow-sm" : ""}`}
      >
        <nav aria-label="Hauptnavigation">
          <div className="flex h-16 sm:h-20 items-center justify-between px-5 sm:px-8 lg:px-12">
            <div className="flex-shrink-0">
              <Link to="/" className="flex items-center gap-4">
                <span className="sr-only">ERGOTHERAPIE UND ZEIT(T)RÄUME</span>
                <LogoSvg classNames="h-7 sm:h-9 lg:h-11 w-auto fill-current text-ergo-500 dark:text-ergo-400 transition-transform duration-300 hover:scale-105" />
                <div className="flex lg:hidden 2xl:flex flex-col">
                  <span className="text-xs sm:text-sm lg:text-base font-medium tracking-wider text-gray-900 dark:text-white">
                    ERGOTHERAPIE
                  </span>
                  <span className="text-xs sm:text-sm lg:text-base font-medium tracking-wider text-ergo-600 dark:text-ergo-400">
                    UND ZEIT(T)RÄUME
                  </span>
                </div>
              </Link>
            </div>

            <div className="flex-grow hidden lg:flex justify-center">
              <div className="flex items-center gap-6 2xl:gap-10">
                <Link
                  to={navigationMenus.home.href}
                  className={`relative px-2 py-2 text-sm lg:text-base tracking-wide transition-all duration-300 group min-h-11 inline-flex items-center ${
                    location.pathname === navigationMenus.home.href
                      ? "text-ergo-600 dark:text-ergo-400 font-medium"
                      : "text-gray-600 dark:text-gray-300 hover:text-ergo-600 dark:hover:text-ergo-400 font-normal"
                  }`}
                >
                  {navigationMenus.home.name}
                  <span
                    className={`absolute bottom-0 left-0 w-full h-0.5 rounded-full transform origin-left transition-transform duration-300 ease-out ${
                      location.pathname === navigationMenus.home.href
                        ? "bg-ergo-400 scale-x-100"
                        : "bg-ergo-300 scale-x-0 group-hover:scale-x-100"
                    }`}
                  />
                </Link>

                {renderDesktopDropdown(
                  "services",
                  navigationMenus.services.label,
                  navigationMenus.services.items,
                  "min-w-[520px]",
                  "grid-cols-2",
                )}
                {renderDesktopDropdown(
                  "about",
                  navigationMenus.about.label,
                  navigationMenus.about.items,
                  "min-w-[280px]",
                  "grid-cols-1",
                )}
              </div>
            </div>

            <div className="flex-shrink-0 hidden lg:block">
              <Link
                to={PATH_CONTACT}
                className="inline-flex items-center gap-2 rounded-full border border-ergo-500/30 dark:border-ergo-400/30
                  bg-ergo-50/60 dark:bg-ergo-900/30 px-5 py-2 min-h-11
                  text-sm font-medium text-ergo-700 dark:text-ergo-300
                  hover:bg-ergo-100 dark:hover:bg-ergo-900/50 hover:border-ergo-500/50 dark:hover:border-ergo-400/50
                  transition-all duration-200"
              >
                <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
                </svg>
                Kontakt
              </Link>
            </div>

            <div className="flex lg:hidden">
              <button
                ref={menuButtonRef}
                type="button"
                className="inline-flex items-center justify-center rounded-md min-h-11 min-w-11 text-ergo-600 dark:text-ergo-400 active:scale-[0.98] transition-transform"
                aria-expanded={mobileMenuOpen}
                aria-controls="mobile-nav-panel"
                onClick={() => setMobileMenuOpen((open) => !open)}
              >
                <span className="sr-only">{mobileMenuOpen ? "Menü schließen" : "Menü öffnen"}</span>
                {mobileMenuOpen ? (
                  <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                ) : (
                  <Bars3Icon className="h-6 w-6" aria-hidden="true" />
                )}
              </button>
            </div>
          </div>

          {/* Backdrop — below header, above page content */}
          <div
            className={`fixed inset-x-0 bottom-0 top-header z-40 bg-black/10 dark:bg-black/20 backdrop-blur-sm transition-opacity duration-300 lg:hidden
              ${mobileMenuOpen ? "opacity-100" : "opacity-0 pointer-events-none"}`}
            onClick={closeMobileMenu}
            aria-hidden="true"
          />

          {/* Mobile menu panel */}
          <div
            ref={mobilePanelRef}
            id="mobile-nav-panel"
            role="dialog"
            aria-modal={mobileMenuOpen}
            aria-label="Navigation"
            aria-hidden={!mobileMenuOpen}
            className={`fixed inset-x-0 bottom-0 top-header z-40 lg:hidden bg-white dark:bg-gray-900 transition-all duration-300 ease-in-out overscroll-none
              ${mobileMenuOpen ? "opacity-100 translate-x-0 pointer-events-auto" : "opacity-0 -translate-x-full pointer-events-none"}`}
          >
            <div className="flex flex-col h-full min-h-0">
              <div className="flex-grow overflow-y-auto px-6 pt-8 pb-8 pb-safe">
                <div className="space-y-6">
                  <Link
                    to={navigationMenus.home.href}
                    className={`block rounded-xl px-4 py-4 text-center transition-colors min-h-11 ${
                      location.pathname === navigationMenus.home.href
                        ? "bg-ergo-50/50 dark:bg-gray-800/50 font-semibold text-ergo-600 dark:text-ergo-400"
                        : "bg-gray-50 dark:bg-gray-800/30 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800/60"
                    }`}
                    onClick={closeMobileMenu}
                  >
                    {navigationMenus.home.name}
                  </Link>

                  {renderMobileAccordion("services", navigationMenus.services.label, navigationMenus.services.items, "grid-cols-1 sm:grid-cols-2")}
                  {renderMobileAccordion("about", navigationMenus.about.label, navigationMenus.about.items, "grid-cols-1")}
                </div>
              </div>

              <div className="p-6 pb-safe">
                <PrimaryButton to={PATH_CONTACT} periodic className="w-full py-3 text-base min-h-11" onClick={closeMobileMenu}>
                  Kontakt
                </PrimaryButton>
              </div>
            </div>
          </div>
        </nav>
      </header>
    </>
  );
};
